import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, XCircle, Clock, Calendar, AlertTriangle,
         RefreshCw, Award, ChevronDown, ChevronUp, Send, Plus, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;
const ax  = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const RATINGS      = ["Exceeds Expectations","Meets Expectations","Needs Improvement","Unacceptable"];
const GOAL_CATS    = ["Quality","Delivery","Innovation","Leadership","Customer Focus","Process Improvement","Learning & Development","Other"];
const GOAL_INIT    = { title:"", description:"", kpi_metric:"", category:"Quality", priority:"Medium", assigned_to_id:"", due_date:"", start_date:"" };

export default function ManagerTeam() {
  const { T } = useTheme();

  const STATUS_COLOR = {
    Approved: { bg:`${T.green}1A`, color:T.green },
    Pending:  { bg:`${T.amber}1A`, color:T.amber },
    Rejected: { bg:`${T.red}1A`,   color:T.red },
  };
  const RATING_COLOR = {
    "Exceeds Expectations": T.green,
    "Meets Expectations":   T.cyan,
    "Needs Improvement":    T.amber,
    "Unacceptable":         T.red,
  };
  const APP_STATUS_COLOR = {
    "Pending":          T.muted,
    "Self-Submitted":   T.amber,
    "Manager-Reviewed": T.green,
    "HR-Finalized":     T.purple,
  };

  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" };
  const inp  = { background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:12.5, padding:"6px 10px", outline:"none", fontFamily:"Outfit,sans-serif", width:"100%", boxSizing:"border-box" };
  const sel  = { ...inp, cursor:"pointer" };

  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id;
  const location   = useLocation();

  const tabFromPath = () => {
    const p = location.pathname;
    if (p.includes("/attendance")) return "regularization";
    if (p.includes("/appraisals")) return "appraisals";
    if (p.includes("/goals"))      return "goals";
    if (p.includes("/compoff"))    return "compoff";
    return "leaves";
  };

  const [tab,           setTab]           = useState(tabFromPath);
  const [leaves,        setLeaves]        = useState([]);
  const [regs,          setRegs]          = useState([]);
  const [appraisals,    setAppraisals]    = useState([]);
  const [teamGoals,     setTeamGoals]     = useState([]);
  const [goalFilter,    setGoalFilter]    = useState("all");
  const [reviewGoalId,  setReviewGoalId]  = useState(null);
  const [reviewNote,    setReviewNote]    = useState("");
  const [reviewingGoal, setReviewingGoal] = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [toast,         setToast]         = useState("");
  const [remarkMap,     setRemarkMap]     = useState({});

  // Goal creation state
  const [goalModal,    setGoalModal]    = useState(false);
  const [goalForm,     setGoalForm]     = useState(GOAL_INIT);
  const [teamEmps,     setTeamEmps]     = useState([]);
  const [creatingGoal, setCreatingGoal] = useState(false);

  // Comp-Off state
  const [compOffs,  setCompOffs]  = useState([]);
  const [coTab,     setCoTab]     = useState("Pending");
  const [coActing,  setCoActing]  = useState({});

  // Appraisal review state
  const [selAppraisal,     setSelAppraisal]     = useState(null);
  const [mgrResponses,     setMgrResponses]     = useState({});
  const [improvements,     setImprovements]     = useState([""]);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [expandedCats,     setExpandedCats]     = useState({});

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const load = async () => {
    if (!platformId) return;
    setLoading(true);
    try {
      const [lRes, rRes, aRes, gRes, coRes] = await Promise.all([
        axios.get(`${API}/leave-requests/team?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/attendance-regularization/team?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/performance/team-appraisals?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/performance/team-goals?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/employee-portal/team-comp-off?platform_user_id=${platformId}`, ax()),
      ]);
      setLeaves(lRes.data    || []);
      setRegs(rRes.data      || []);
      setAppraisals(aRes.data|| []);
      setTeamGoals(gRes.data || []);
      setCompOffs(coRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Load direct reports for goal assignment dropdown
  const loadTeamEmps = async () => {
    if (!platformId) return;
    try {
      const r = await axios.get(`${API}/performance/direct-reports?platform_user_id=${platformId}`, ax());
      setTeamEmps(r.data || []);
    } catch {}
  };

  useEffect(() => { load(); loadTeamEmps(); }, [platformId]);
  useEffect(() => { setTab(tabFromPath()); }, [location.pathname]);

  const openGoalModal = () => {
    setGoalForm(GOAL_INIT);
    setGoalModal(true);
  };

  const createGoal = async () => {
    if (!goalForm.title.trim())      return flash("❌ Goal title is required");
    if (!goalForm.assigned_to_id)    return flash("❌ Please select an assignee");
    setCreatingGoal(true);
    try {
      await axios.post(
        `${API}/performance/goals/manager-create?platform_user_id=${platformId}`,
        { ...goalForm, assigned_to_id: Number(goalForm.assigned_to_id) },
        ax()
      );
      flash("✅ Goal created and assigned successfully");
      setGoalModal(false);
      setGoalForm(GOAL_INIT);
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error creating goal")); }
    finally { setCreatingGoal(false); }
  };

  const openAppraisal = (a) => {
    setSelAppraisal(a);
    const init = {};
    (a.responses||[]).forEach(r => {
      init[r.id] = { manager_evaluation:r.manager_evaluation||"", manager_rating:r.manager_rating||"", appraiser_notes:r.appraiser_notes||"" };
    });
    setMgrResponses(init);
    const imp = (a.improvements||[]).filter(i=>i.added_by==="manager").map(i=>i.text);
    setImprovements(imp.length ? imp : [""]);
    const cats = {};
    [...new Set((a.responses||[]).map(r=>r.category))].forEach(c => { cats[c] = true; });
    setExpandedCats(cats);
  };

  const reviewLeave = async (id, status) => {
    try {
      await axios.put(`${API}/leave-requests/${id}/manager-review?platform_user_id=${platformId}`,
        { status, remarks: remarkMap[`l_${id}`] || "" }, ax());
      flash(`✅ Leave ${status}`);
      setRemarkMap(p => { const n={...p}; delete n[`l_${id}`]; return n; });
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
  };

  const reviewReg = async (id, status) => {
    try {
      await axios.put(`${API}/attendance-regularization/${id}/review?platform_user_id=${platformId}`,
        { status, remarks: remarkMap[`r_${id}`] || "" }, ax());
      flash(`✅ Regularization ${status}${status === "Approved" ? " — attendance updated" : ""}`);
      setRemarkMap(p => { const n={...p}; delete n[`r_${id}`]; return n; });
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
  };

  const submitManagerReview = async () => {
    if (!selAppraisal) return;
    setSubmittingReview(true);
    try {
      const payload = {
        responses: Object.entries(mgrResponses).map(([rid, data]) => ({
          response_id: parseInt(rid), manager_evaluation:data.manager_evaluation,
          manager_rating:data.manager_rating, appraiser_notes:data.appraiser_notes,
        })),
        improvements: improvements.filter(t=>t.trim()).map(t=>({ text:t, category_id:null })),
      };
      await axios.put(`${API}/performance/appraisals/${selAppraisal.id}/manager-review?platform_user_id=${platformId}`, payload, ax());
      flash("✅ Manager review submitted successfully");
      setSelAppraisal(null); load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
    finally { setSubmittingReview(false); }
  };

  const reviewGoal = async (goalId, approved) => {
    setReviewingGoal(true);
    try {
      await axios.put(`${API}/performance/goals/${goalId}/review?platform_user_id=${platformId}`,
        { approved, manager_note: reviewNote }, ax());
      flash(`✅ Goal ${approved?"approved":"sent back for revision"}`);
      setReviewGoalId(null); setReviewNote(""); load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail||"Error")); }
    finally { setReviewingGoal(false); }
  };

  const approveCompOff = async (id) => {
    setCoActing(p=>({...p,[id]:"approving"}));
    try {
      await axios.put(`${API}/employee-portal/team-comp-off/${id}/approve?platform_user_id=${platformId}`, {}, ax());
      flash("✅ Comp-off approved — leave credited to employee.");
      setCompOffs(p=>p.map(x=>x.id===id?{...x,status:"Approved"}:x));
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Failed")); }
    finally { setCoActing(p=>({...p,[id]:null})); }
  };

  const rejectCompOff = async (id) => {
    setCoActing(p=>({...p,[id]:"rejecting"}));
    try {
      await axios.put(`${API}/employee-portal/team-comp-off/${id}/reject?platform_user_id=${platformId}`, {}, ax());
      flash("Comp-off request rejected.");
      setCompOffs(p=>p.map(x=>x.id===id?{...x,status:"Rejected"}:x));
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Failed")); }
    finally { setCoActing(p=>({...p,[id]:null})); }
  };

  const pendingLeaves     = leaves.filter(l => l.status === "Pending");
  const pendingRegs       = regs.filter(r => r.status === "Pending");
  const pendingAppraisals = appraisals.filter(a => a.status === "Self-Submitted");
  const pendingCompOffs   = compOffs.filter(c => c.status === "Pending");

  const TabBtn = ({ id, label, count, color = T.cyan }) => (
    <button onClick={() => setTab(id)} style={{
      padding:"10px 18px", fontSize:13, fontWeight:500, fontFamily:"Outfit,sans-serif",
      color:tab===id?color:T.muted, background:"none", border:"none",
      borderBottom:tab===id?`2px solid ${color}`:"2px solid transparent",
      cursor:"pointer", display:"flex", alignItems:"center", gap:7,
    }}>
      {label}
      {count > 0 && (
        <span style={{ fontSize:10.5, padding:"1px 7px", borderRadius:10,
          background:tab===id?`${color}26`:T.toggle_bg, color:tab===id?color:T.muted,
          fontFamily:"JetBrains Mono,monospace", fontWeight:700 }}>
          {count}
        </span>
      )}
    </button>
  );

  const ReviewRow = ({ id, prefix, onApprove, onReject, status }) => (
    <div style={{ marginTop:8, display:"flex", gap:7, alignItems:"flex-start", flexWrap:"wrap" }}>
      {status === "Pending" && (
        <>
          <input value={remarkMap[`${prefix}_${id}`] || ""}
            onChange={e => setRemarkMap(p => ({ ...p, [`${prefix}_${id}`]: e.target.value }))}
            placeholder="Remarks (optional)…" style={{ ...inp, flex:1, minWidth:140 }}/>
          <button onClick={onApprove}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, border:`1px solid ${T.green}4D`, background:`${T.green}14`, color:T.green, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer", whiteSpace:"nowrap" }}>
            <CheckCircle size={12}/> Approve
          </button>
          <button onClick={onReject}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, border:`1px solid ${T.red}4D`, background:`${T.red}14`, color:T.red, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer", whiteSpace:"nowrap" }}>
            <XCircle size={12}/> Reject
          </button>
        </>
      )}
    </div>
  );

  // ── Label style for modal inputs ──────────────────────────────────
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:5,
                textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:"Outfit,sans-serif" };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:11, color:T.purple, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>MY TEAM</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Team Overview</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>Review leave requests, attendance corrections, appraisals and comp-off</p>
        </div>
        <button onClick={load} style={{ display:"flex", alignItems:"center", gap:6, background:T.inp_bg, border:`1px solid ${T.cyan}1F`, borderRadius:8, color:T.muted, padding:"8px 14px", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
          <RefreshCw size={13}/> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"Pending Leaves",      val:pendingLeaves.length,     color:T.amber },
          { label:"Pending Corrections", val:pendingRegs.length,       color:T.red },
          { label:"Pending Appraisals",  val:pendingAppraisals.length, color:T.purple },
          { label:"Goals to Review",     val:teamGoals.filter(g=>g.status==="Submitted").length, color:T.green },
          { label:"Comp-Off Requests",   val:pendingCompOffs.length,   color:T.amber },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ ...card, padding:16, borderTop:`3px solid ${color}` }}>
            <div style={{ fontSize:26, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
            <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ ...card, marginBottom:0 }}>
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}`, padding:"4px 16px 0", gap:4, flexWrap:"wrap" }}>
          <TabBtn id="leaves"         label="Leave Requests"        count={pendingLeaves.length}     color={T.amber}/>
          <TabBtn id="regularization" label="Attendance Correction" count={pendingRegs.length}       color={T.red}/>
          <TabBtn id="appraisals"     label="Appraisals"            count={pendingAppraisals.length} color={T.purple}/>
          <TabBtn id="goals"          label="Goals & KPIs"          count={teamGoals.filter(g=>g.status==="Submitted").length} color={T.green}/>
          <TabBtn id="compoff"        label="Comp-Off"              count={pendingCompOffs.length}   color={T.amber}/>
        </div>

        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>Loading…</div>
        ) : (
          <>
          {/* ── LEAVE REQUESTS ── */}
          {tab === "leaves" && (
            <div>
              {leaves.length === 0 ? (
                <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>
                  <Calendar size={32} style={{ color:T.dim, marginBottom:10 }}/>
                  <div>No leave requests from your team</div>
                </div>
              ) : leaves.map(r => {
                const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
                return (
                  <div key={r.id} style={{ padding:"16px 20px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                    <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>
                        {(r.employee_name||"").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                          <span style={{ fontSize:14, fontWeight:600 }}>{r.employee_name}</span>
                          <span style={{ fontSize:11, color:T.muted }}>{r.department}</span>
                          {r.is_lop && <span style={{ fontSize:10.5, padding:"2px 7px", borderRadius:8, background:`${T.red}1A`, color:T.red, fontWeight:600 }}>⚠️ LOP{r.lop_days > 0 ? ` (${r.lop_days}d)` : ""}</span>}
                        </div>
                        <div style={{ display:"flex", gap:16, marginTop:6, flexWrap:"wrap" }}>
                          <div>
                            <span style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Leave Type</span>
                            <div style={{ fontSize:12.5, marginTop:2 }}>
                              <span style={{ padding:"2px 8px", borderRadius:8, background:`${r.leave_color||T.cyan}33`, color:r.leave_color||T.cyan, fontWeight:600, fontSize:12 }}>{r.leave_type}</span>
                            </div>
                          </div>
                          <div>
                            <span style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Duration</span>
                            <div style={{ fontSize:12.5, marginTop:2, color:T.text }}>
                              {r.start_date} → {r.end_date}
                              <span style={{ marginLeft:8, fontFamily:"JetBrains Mono,monospace", color:T.cyan, fontWeight:700 }}>{r.days}d</span>
                              {r.is_lop && r.lop_days > 0 && (
                                <span style={{ marginLeft:6, fontSize:11, color:T.red, fontFamily:"JetBrains Mono,monospace" }}>({r.lop_days}d LOP)</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <span style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Reason</span>
                            <div style={{ fontSize:12.5, marginTop:2, color:T.muted }}>{r.reason}</div>
                          </div>
                        </div>
                        <ReviewRow id={r.id} prefix="l" status={r.status}
                          onApprove={() => reviewLeave(r.id,"Approved")}
                          onReject={()  => reviewLeave(r.id,"Rejected")}/>
                        {r.manager_remarks && <div style={{ marginTop:6, fontSize:12, color:T.amber }}>Your remarks: {r.manager_remarks}</div>}
                      </div>
                      <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, background:sc.bg, color:sc.color, flexShrink:0 }}>{r.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── ATTENDANCE CORRECTION ── */}
          {tab === "regularization" && (
            <div>
              {regs.length === 0 ? (
                <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>
                  <Clock size={32} style={{ color:T.dim, marginBottom:10 }}/>
                  <div>No attendance correction requests from your team</div>
                </div>
              ) : regs.map(r => {
                const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
                return (
                  <div key={r.id} style={{ padding:"16px 20px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                    <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${T.red},${T.amber})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>
                        {(r.employee_name||"").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                          <span style={{ fontSize:14, fontWeight:600 }}>{r.employee_name}</span>
                          <span style={{ fontSize:11, color:T.muted }}>{r.department}</span>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:12, marginTop:8 }}>
                          <div style={{ background:T.surface_faint, border:`1px solid ${T.cyan}0F`, borderRadius:8, padding:"8px 12px" }}>
                            <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Date</div>
                            <div style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace", color:T.text, marginTop:3 }}>{r.date}</div>
                          </div>
                          <div style={{ background:`${T.amber}0F`, border:`1px solid ${T.amber}1F`, borderRadius:8, padding:"8px 12px" }}>
                            <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Current</div>
                            <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>In: {r.existing_in||"—"} / Out: {r.existing_out||"—"}</div>
                          </div>
                          <div style={{ background:`${T.green}0F`, border:`1px solid ${T.green}1F`, borderRadius:8, padding:"8px 12px" }}>
                            <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Requested</div>
                            <div style={{ fontSize:12, color:T.green, marginTop:3 }}>In: {r.requested_in||"—"} / Out: {r.requested_out||"—"}</div>
                          </div>
                        </div>
                        <div style={{ marginTop:8, fontSize:12.5, color:T.muted }}><span style={{ color:T.dim }}>Reason:</span> {r.reason}</div>
                        <ReviewRow id={r.id} prefix="r" status={r.status}
                          onApprove={() => reviewReg(r.id,"Approved")}
                          onReject={()  => reviewReg(r.id,"Rejected")}/>
                        {r.review_remarks && <div style={{ marginTop:6, fontSize:12, color:T.amber }}>Remarks: {r.review_remarks}</div>}
                      </div>
                      <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, background:sc.bg, color:sc.color, flexShrink:0 }}>{r.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── APPRAISALS ── */}
          {tab === "appraisals" && !selAppraisal && (
            <div>
              {appraisals.length === 0 ? (
                <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>
                  <Award size={32} style={{ color:T.dim, marginBottom:10 }}/>
                  <div>No appraisals assigned to your team yet</div>
                </div>
              ) : appraisals.map(a => {
                const sc = { color: APP_STATUS_COLOR[a.status]||T.muted };
                const canReview = a.status === "Self-Submitted";
                return (
                  <div key={a.id} style={{ padding:"16px 20px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                    <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${T.purple},${T.cyan})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>
                        {(a.employee_name||"").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                          <span style={{ fontSize:14, fontWeight:600 }}>{a.employee_name}</span>
                          <span style={{ fontSize:11, color:T.muted }}>{a.designation}</span>
                          <span style={{ fontSize:11, color:T.muted }}>{a.department}</span>
                        </div>
                        <div style={{ fontSize:12, color:T.muted, marginTop:4 }}>{a.cycle_name} · {a.review_period||""}</div>
                        {a.self_submitted_at && (
                          <div style={{ fontSize:11.5, color:T.dim, marginTop:4 }}>
                            Submitted: {new Date(a.self_submitted_at).toLocaleDateString("en-IN")}
                          </div>
                        )}
                        {canReview && (
                          <button onClick={() => openAppraisal(a)}
                            style={{ marginTop:10, display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:`1px solid ${T.purple}4D`, background:`${T.purple}14`, color:T.purple, fontSize:12.5, fontFamily:"Outfit,sans-serif", cursor:"pointer", fontWeight:600 }}>
                            <Award size={12}/> Review Appraisal
                          </button>
                        )}
                        {!canReview && a.status !== "Pending" && (
                          <button onClick={() => openAppraisal(a)}
                            style={{ marginTop:10, display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:`1px solid ${T.cyan}26`, background:"none", color:T.cyan, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer" }}>
                            View Details
                          </button>
                        )}
                      </div>
                      <div style={{ textAlign:"right", flexShrink:0 }}>
                        <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, background:`${sc.color}26`, color:sc.color }}>
                          {a.status.replace(/-/g," ")}
                        </span>
                        {a.overall_rating && (
                          <div style={{ marginTop:6, fontSize:11, color:RATING_COLOR[a.overall_rating]||T.muted }}>{a.overall_rating}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════
              GOALS & KPIs TAB
          ══════════════════════════════════════════════════════ */}
          {tab === "goals" && (() => {
            const S_COLOR = { "Not Started":T.muted,"In Progress":T.cyan,"Submitted":T.amber,"Completed":T.green,"Rejected":T.red };
            const P_COLOR = { High:T.red, Medium:T.amber, Low:T.green };
            const filtered = goalFilter==="all" ? teamGoals : teamGoals.filter(g=>g.status===goalFilter);
            return (
              <div>
                {/* ── Filter bar + Create button ── */}
                <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.toggle_bg}`, display:"flex", gap:7, flexWrap:"wrap", alignItems:"center" }}>
                  <div style={{ display:"flex", gap:7, flexWrap:"wrap", flex:1 }}>
                    {["all","Submitted","In Progress","Not Started","Completed"].map(s => (
                      <button key={s} onClick={()=>setGoalFilter(s)}
                        style={{ padding:"4px 12px", borderRadius:20, fontSize:11.5, fontFamily:"Outfit,sans-serif", cursor:"pointer",
                          border:`1px solid ${goalFilter===s?`${T.cyan}4D`:T.toggle_bg}`,
                          background:goalFilter===s?`${T.cyan}14`:T.surface_faint,
                          color:goalFilter===s?T.cyan:T.muted }}>
                        {s==="all"?"All":s}
                        {s!=="all"&&<span style={{ marginLeft:4, fontFamily:"JetBrains Mono,monospace", fontSize:10 }}>{teamGoals.filter(g=>g.status===s).length}</span>}
                      </button>
                    ))}
                  </div>
                  {/* ── Create Goal button ── */}
                  <button onClick={openGoalModal}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8,
                      border:`1px solid ${T.green}4D`, background:`${T.green}14`,
                      color:T.green, fontSize:12.5, fontFamily:"Outfit,sans-serif",
                      cursor:"pointer", fontWeight:600, whiteSpace:"nowrap", flexShrink:0 }}>
                    <Plus size={13}/> Create Goal
                  </button>
                </div>

                {filtered.length === 0 ? (
                  <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>
                    <div style={{ fontSize:32, marginBottom:10 }}>🎯</div>
                    <div style={{ marginBottom:14 }}>No goals for your team{goalFilter!=="all"?` with status "${goalFilter}`:""}</div>
                    <button onClick={openGoalModal}
                      style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 18px", borderRadius:8,
                        border:`1px solid ${T.green}4D`, background:`${T.green}14`,
                        color:T.green, fontSize:13, fontFamily:"Outfit,sans-serif", cursor:"pointer", fontWeight:600 }}>
                      <Plus size={14}/> Create first goal
                    </button>
                  </div>
                ) : filtered.map(g => (
                  <div key={g.id} style={{ padding:"16px 20px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                    <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${T.green},${T.cyan})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>
                        {(g.assigned_to_name||"").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
                          <span style={{ fontSize:14, fontWeight:600 }}>{g.assigned_to_name}</span>
                          <span style={{ fontSize:11, color:T.muted }}>{g.assigned_to_dept}</span>
                          <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${P_COLOR[g.priority]||T.amber}26`, color:P_COLOR[g.priority]||T.amber }}>{g.priority}</span>
                        </div>
                        <div style={{ fontSize:13, fontWeight:500, color:T.text, marginBottom:6 }}>{g.title}</div>
                        {g.kpi_metric && <div style={{ fontSize:12, color:T.dim, marginBottom:6 }}>KPI: <span style={{ color:T.muted }}>{g.kpi_metric}</span></div>}
                        <div style={{ marginBottom:8 }}>
                          <div style={{ height:5, background:T.toggle_bg, borderRadius:3, overflow:"hidden" }}>
                            <div style={{ height:"100%", width:`${g.progress}%`, background:`linear-gradient(90deg,${T.cyan},${T.green})`, borderRadius:3 }}/>
                          </div>
                          <div style={{ fontSize:11, color:T.muted, marginTop:3 }}>{g.progress}% complete · Due: {g.due_date||"—"}</div>
                        </div>
                        {g.completion_note && (
                          <div style={{ padding:"7px 10px", background:`${T.amber}0F`, borderRadius:8, border:`1px solid ${T.amber}1A`, fontSize:12, color:T.amber, marginBottom:6 }}>
                            Employee note: {g.completion_note}
                          </div>
                        )}
                        {g.status === "Submitted" && reviewGoalId !== g.id && (
                          <button onClick={()=>{ setReviewGoalId(g.id); setReviewNote(""); }}
                            style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, border:`1px solid ${T.green}4D`, background:`${T.green}14`, color:T.green, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer" }}>
                            Review Completion →
                          </button>
                        )}
                        {reviewGoalId === g.id && (
                          <div style={{ marginTop:8, padding:"12px", background:T.surface_faint, borderRadius:9, border:`1px solid ${T.border}` }}>
                            <textarea value={reviewNote} onChange={e=>setReviewNote(e.target.value)} rows={2}
                              placeholder="Feedback for employee (optional)…"
                              style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:7, color:T.text, fontSize:12.5, padding:"7px 10px", outline:"none", fontFamily:"Outfit,sans-serif", resize:"vertical", boxSizing:"border-box", marginBottom:8 }}/>
                            <div style={{ display:"flex", gap:7 }}>
                              <button onClick={()=>setReviewGoalId(null)} style={{ padding:"6px 12px", borderRadius:7, border:`1px solid ${T.toggle_bg}`, background:"none", color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12 }}>Cancel</button>
                              <button disabled={reviewingGoal} onClick={()=>reviewGoal(g.id, false)}
                                style={{ padding:"6px 12px", borderRadius:7, border:`1px solid ${T.red}4D`, background:`${T.red}14`, color:T.red, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, opacity:reviewingGoal?0.7:1 }}>
                                Send Back
                              </button>
                              <button disabled={reviewingGoal} onClick={()=>reviewGoal(g.id, true)}
                                style={{ flex:1, padding:"6px 12px", borderRadius:7, border:"none", background:`linear-gradient(135deg,${T.green},${T.cyan})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700, opacity:reviewingGoal?0.7:1 }}>
                                ✓ Approve & Complete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, flexShrink:0, background:`${S_COLOR[g.status]||T.muted}26`, color:S_COLOR[g.status]||T.muted }}>
                        {g.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ── COMP-OFF TAB ── */}
          {tab === "compoff" && (
            <div>
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.toggle_bg}`, display:"flex", gap:7, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ fontSize:12, color:T.muted, marginRight:4 }}>Filter:</span>
                {["Pending","Approved","Rejected","All"].map(s => (
                  <button key={s} onClick={()=>setCoTab(s)}
                    style={{ padding:"4px 14px", borderRadius:20, fontSize:11.5, fontFamily:"Outfit,sans-serif", cursor:"pointer",
                      border:`1px solid ${coTab===s?`${T.amber}59`:T.toggle_bg}`,
                      background:coTab===s?`${T.amber}1A`:T.surface_faint,
                      color:coTab===s?T.amber:T.muted }}>
                    {s}
                    {s!=="All" && <span style={{ marginLeft:4, fontFamily:"JetBrains Mono,monospace", fontSize:10 }}>{compOffs.filter(r=>r.status===s).length}</span>}
                  </button>
                ))}
              </div>
              <div style={{ padding:"10px 16px", background:`${T.amber}0A`, borderBottom:`1px solid ${T.amber}14`, fontSize:12, color:T.muted }}>
                <span style={{ color:T.amber }}>ℹ️</span> Either you (manager) or HR can approve. Whichever approves first credits the leave balance.
              </div>
              {(coTab==="All" ? compOffs : compOffs.filter(r=>r.status===coTab)).length === 0 ? (
                <div style={{ padding:40, textAlign:"center", color:T.muted, fontSize:13 }}>
                  <Clock size={32} style={{ color:T.dim, marginBottom:10 }}/>
                  <div>No {coTab.toLowerCase()} comp-off requests from your team</div>
                </div>
              ) : (
                (coTab==="All" ? compOffs : compOffs.filter(r=>r.status===coTab)).map(r => {
                  const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
                  return (
                    <div key={r.id} style={{ padding:"16px 20px", borderBottom:`1px solid ${T.toggle_bg}` }}>
                      <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                        <div style={{ textAlign:"center", padding:"10px 12px", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:10, flexShrink:0, minWidth:70 }}>
                          <div style={{ fontSize:10, color:T.dim }}>{new Date(r.worked_date+"T00:00:00").toLocaleDateString("en-IN",{month:"short"})}</div>
                          <div style={{ fontSize:22, fontWeight:900, color:T.text, lineHeight:1 }}>{new Date(r.worked_date+"T00:00:00").getDate()}</div>
                          <div style={{ fontSize:10, color:T.dim }}>{new Date(r.worked_date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"short"})}</div>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
                            <span style={{ fontSize:14, fontWeight:600 }}>{r.first_name} {r.last_name}</span>
                            <span style={{ fontSize:11, color:T.muted }}>{r.employee_code}</span>
                            {r.department_name && <span style={{ fontSize:11, color:T.muted }}>{r.department_name}</span>}
                            <span style={{ fontSize:11, padding:"2px 8px", borderRadius:100,
                              background:r.is_half_day?`${T.purple}26`:`${T.cyan}1A`,
                              color:r.is_half_day?T.purple:T.cyan, fontWeight:700 }}>
                              {r.is_half_day?"Half Day · 0.5d":"Full Day · 1d"}
                            </span>
                          </div>
                          <div style={{ fontSize:12.5, color:T.muted, marginBottom:4 }}>{r.reason}</div>
                          {r.remarks && <div style={{ fontSize:12, color:T.amber }}>Remarks: {r.remarks}</div>}
                          <div style={{ fontSize:11, color:T.dim, marginTop:4 }}>
                            Submitted: {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—"}
                          </div>
                          {r.status === "Pending" && (
                            <div style={{ display:"flex", gap:7, marginTop:10, flexWrap:"wrap" }}>
                              <button onClick={() => approveCompOff(r.id)} disabled={coActing[r.id]==="approving"}
                                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", borderRadius:7, border:`1px solid ${T.green}4D`, background:`${T.green}14`, color:T.green, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer", fontWeight:600, opacity:coActing[r.id]==="approving"?0.6:1 }}>
                                <CheckCircle size={12}/>{coActing[r.id]==="approving"?"Approving…":"Approve"}
                              </button>
                              <button onClick={() => rejectCompOff(r.id)} disabled={coActing[r.id]==="rejecting"}
                                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", borderRadius:7, border:`1px solid ${T.red}4D`, background:`${T.red}14`, color:T.red, fontSize:12, fontFamily:"Outfit,sans-serif", cursor:"pointer", fontWeight:600, opacity:coActing[r.id]==="rejecting"?0.6:1 }}>
                                <XCircle size={12}/>{coActing[r.id]==="rejecting"?"Rejecting…":"Reject"}
                              </button>
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, background:sc.bg, color:sc.color, flexShrink:0 }}>{r.status}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── APPRAISAL REVIEW FORM ── */}
          {tab === "appraisals" && selAppraisal && (
            <div style={{ padding:20 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <button onClick={() => setSelAppraisal(null)}
                  style={{ background:"none", border:`1px solid ${T.cyan}26`, borderRadius:7, color:T.cyan, padding:"5px 12px", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12 }}>
                  ← Back
                </button>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:T.text }}>{selAppraisal.employee_name}</div>
                  <div style={{ fontSize:12, color:T.muted }}>{selAppraisal.designation} · {selAppraisal.cycle_name}</div>
                </div>
                <span style={{ marginLeft:"auto", fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600,
                  background:`${APP_STATUS_COLOR[selAppraisal.status]||T.muted}26`, color:APP_STATUS_COLOR[selAppraisal.status]||T.muted }}>
                  {selAppraisal.status.replace(/-/g," ")}
                </span>
              </div>
              {(() => {
                const cats = {};
                (selAppraisal.responses||[]).forEach(r => {
                  const cat = r.category || "General";
                  if (!cats[cat]) cats[cat] = [];
                  cats[cat].push(r);
                });
                return Object.entries(cats).map(([catName, responses]) => (
                  <div key={catName} style={{ background:T.surface_faint, border:`1px solid ${T.cyan}0F`, borderRadius:10, marginBottom:14, overflow:"hidden" }}>
                    <div style={{ padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center",
                      background:`${T.cyan}08`, cursor:"pointer", borderBottom:expandedCats[catName]?`1px solid ${T.cyan}0F`:"none" }}
                      onClick={() => setExpandedCats(p=>({...p,[catName]:!p[catName]}))}>
                      <span style={{ fontSize:13, fontWeight:700, color:T.cyan, textTransform:"uppercase", letterSpacing:"0.07em" }}>{catName}</span>
                      {expandedCats[catName] ? <ChevronUp size={14} style={{ color:T.muted }}/> : <ChevronDown size={14} style={{ color:T.muted }}/>}
                    </div>
                    {expandedCats[catName] && responses.map((r, ri) => {
                      const mgr   = mgrResponses[r.id] || {};
                      const canEdit = selAppraisal.status === "Self-Submitted";
                      return (
                        <div key={r.id} style={{ padding:"16px", borderBottom:ri<responses.length-1?`1px solid ${T.toggle_bg}`:"none" }}>
                          <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:12 }}>{r.question_title}</div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                            <div style={{ background:`${T.cyan}0A`, border:`1px solid ${T.cyan}14`, borderRadius:9, padding:12 }}>
                              <div style={{ fontSize:10.5, color:T.cyan, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Employee Self-Evaluation</div>
                              <div style={{ fontSize:12.5, color:T.text, lineHeight:1.6, minHeight:40 }}>
                                {r.self_evaluation || <em style={{ color:T.dim }}>Not filled</em>}
                              </div>
                              {r.self_rating && (
                                <div style={{ marginTop:8 }}>
                                  <span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:`${RATING_COLOR[r.self_rating]||T.muted}26`, color:RATING_COLOR[r.self_rating]||T.muted }}>{r.self_rating}</span>
                                </div>
                              )}
                            </div>
                            <div style={{ background:`${T.purple}0A`, border:`1px solid ${T.purple}1A`, borderRadius:9, padding:12 }}>
                              <div style={{ fontSize:10.5, color:T.purple, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Your Evaluation</div>
                              <textarea value={mgr.manager_evaluation||""} disabled={!canEdit} rows={3}
                                onChange={e => setMgrResponses(p=>({...p,[r.id]:{...p[r.id],manager_evaluation:e.target.value}}))}
                                placeholder={canEdit?"Add your evaluation…":"—"}
                                style={{ width:"100%", background:canEdit?T.inp_bg:"transparent", border:canEdit?`1px solid ${T.purple}33`:"none", borderRadius:7, color:T.text, fontSize:12.5, padding:"7px 10px", outline:"none", fontFamily:"Outfit,sans-serif", resize:"vertical", boxSizing:"border-box", opacity:canEdit?1:0.7 }}/>
                              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:8 }}>
                                {RATINGS.map(rat => (
                                  <button key={rat} disabled={!canEdit}
                                    onClick={() => setMgrResponses(p=>({...p,[r.id]:{...p[r.id],manager_rating:rat}}))}
                                    style={{ fontSize:10.5, padding:"3px 8px", borderRadius:6, cursor:canEdit?"pointer":"default",
                                      border:`1px solid ${mgr.manager_rating===rat?RATING_COLOR[rat]||T.purple:T.toggle_bg}`,
                                      background:mgr.manager_rating===rat?`${RATING_COLOR[rat]||T.purple}26`:T.surface_faint,
                                      color:mgr.manager_rating===rat?RATING_COLOR[rat]||T.purple:T.muted,
                                      fontFamily:"Outfit,sans-serif", transition:"all 0.15s" }}>
                                    {rat}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
              {selAppraisal.status === "Self-Submitted" && (
                <div style={{ background:`${T.amber}0D`, border:`1px solid ${T.amber}26`, borderRadius:10, padding:16, marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.amber, marginBottom:12 }}>
                    <AlertTriangle size={13} style={{ display:"inline", marginRight:6, verticalAlign:"middle" }}/>
                    Areas for Improvement
                  </div>
                  {improvements.map((imp, i) => (
                    <div key={i} style={{ display:"flex", gap:7, marginBottom:8 }}>
                      <input value={imp} placeholder={`Improvement area ${i+1}…`} style={{ ...inp, flex:1 }}
                        onChange={e => setImprovements(p => p.map((x,j)=>j===i?e.target.value:x))}/>
                      {improvements.length > 1 && (
                        <button onClick={() => setImprovements(p=>p.filter((_,j)=>j!==i))}
                          style={{ background:"none", border:`1px solid ${T.red}33`, borderRadius:7, color:T.red, padding:"4px 8px", cursor:"pointer", fontSize:12 }}>✕</button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setImprovements(p=>[...p,""])}
                    style={{ fontSize:12, color:T.amber, background:"none", border:`1px solid ${T.amber}33`, borderRadius:7, padding:"5px 12px", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                    + Add Area
                  </button>
                </div>
              )}
              {selAppraisal.status === "Self-Submitted" && (
                <div style={{ display:"flex", justifyContent:"flex-end", gap:12 }}>
                  <button onClick={() => setSelAppraisal(null)}
                    style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
                    Cancel
                  </button>
                  <button onClick={submitManagerReview} disabled={submittingReview}
                    style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 22px", borderRadius:8, border:"none",
                      background:`linear-gradient(135deg,${T.purple},${T.cyan})`, color:"#fff",
                      fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, cursor:"pointer", opacity:submittingReview?0.7:1 }}>
                    <Send size={13}/>{submittingReview?"Submitting…":"Submit Manager Review"}
                  </button>
                </div>
              )}
            </div>
          )}
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          CREATE GOAL MODAL
      ══════════════════════════════════════════════════════════ */}
      {goalModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9999,
          display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif", padding:16 }}
          onClick={e => { if(e.target===e.currentTarget && !creatingGoal) setGoalModal(false); }}>
          <div style={{ background:T.card, border:`1px solid ${T.green}40`, borderRadius:14,
            width:520, maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column",
            boxShadow:`0 20px 60px rgba(0,0,0,0.4)` }}>

            {/* Modal header */}
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`,
              background:`${T.green}0A`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              <div>
                <div style={{ fontSize:10, color:T.green, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>GOALS & KPIs</div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:T.text }}>Create Goal for Team Member</h3>
              </div>
              <button onClick={() => setGoalModal(false)} disabled={creatingGoal}
                style={{ background:T.toggle_bg, border:"none", borderRadius:"50%", width:32, height:32,
                  display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:T.muted }}>
                <X size={14}/>
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding:24, overflowY:"auto", display:"flex", flexDirection:"column", gap:16 }}>

              {/* Assign To */}
              <div>
                <label style={lbl}>Assign To *</label>
                {teamEmps.length === 0 ? (
                  <div style={{ padding:"10px 12px", background:`${T.amber}0F`, border:`1px solid ${T.amber}26`, borderRadius:8, fontSize:12.5, color:T.amber }}>
                    No direct reports found. Employees must be assigned to you as manager.
                  </div>
                ) : (
                  <select value={goalForm.assigned_to_id}
                    onChange={e => setGoalForm(p=>({...p, assigned_to_id:e.target.value}))}
                    style={sel}>
                    <option value="">Select team member…</option>
                    {teamEmps.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.name}{e.employee_code ? ` (${e.employee_code})` : ""}{e.department ? ` — ${e.department}` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Title */}
              <div>
                <label style={lbl}>Goal Title *</label>
                <input value={goalForm.title} placeholder="e.g. Improve code review turnaround time"
                  onChange={e => setGoalForm(p=>({...p, title:e.target.value}))}
                  style={inp}/>
              </div>

              {/* Description */}
              <div>
                <label style={lbl}>Description</label>
                <textarea value={goalForm.description} rows={2} placeholder="What does success look like?"
                  onChange={e => setGoalForm(p=>({...p, description:e.target.value}))}
                  style={{ ...inp, resize:"vertical" }}/>
              </div>

              {/* KPI Metric */}
              <div>
                <label style={lbl}>KPI Metric</label>
                <input value={goalForm.kpi_metric} placeholder="e.g. Review PRs within 24 hours, 95% of the time"
                  onChange={e => setGoalForm(p=>({...p, kpi_metric:e.target.value}))}
                  style={inp}/>
              </div>

              {/* Category + Priority */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div>
                  <label style={lbl}>Category</label>
                  <select value={goalForm.category} onChange={e => setGoalForm(p=>({...p, category:e.target.value}))} style={sel}>
                    {GOAL_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Priority</label>
                  <select value={goalForm.priority} onChange={e => setGoalForm(p=>({...p, priority:e.target.value}))} style={sel}>
                    {["High","Medium","Low"].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div>
                  <label style={lbl}>Start Date</label>
                  <input type="date" value={goalForm.start_date}
                    onChange={e => setGoalForm(p=>({...p, start_date:e.target.value}))}
                    style={inp}/>
                </div>
                <div>
                  <label style={lbl}>Due Date</label>
                  <input type="date" value={goalForm.due_date}
                    onChange={e => setGoalForm(p=>({...p, due_date:e.target.value}))}
                    style={inp}/>
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div style={{ padding:"16px 24px", borderTop:`1px solid ${T.border}`,
              display:"flex", gap:10, justifyContent:"flex-end", flexShrink:0 }}>
              <button onClick={() => setGoalModal(false)} disabled={creatingGoal}
                style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.border}`,
                  background:"none", color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600 }}>
                Cancel
              </button>
              <button onClick={createGoal} disabled={creatingGoal || !goalForm.title.trim() || !goalForm.assigned_to_id}
                style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 22px", borderRadius:8,
                  border:"none", background:(!goalForm.title.trim()||!goalForm.assigned_to_id)?T.toggle_bg:T.green,
                  color:(!goalForm.title.trim()||!goalForm.assigned_to_id)?T.dim:"#fff",
                  cursor:(!goalForm.title.trim()||!goalForm.assigned_to_id)?"not-allowed":"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, opacity:creatingGoal?0.7:1 }}>
                <Plus size={14}/>{creatingGoal ? "Creating…" : "Create & Assign Goal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px",
          borderRadius:10, fontSize:13, fontWeight:600, zIndex:10000,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}